<template>
  <div class="live-video-widget" ref="content" :class="{ 'blinds-closed': blindsClosed }" @mouseover="widgetMouseOver">
    <video-issue-report-modal v-if="showVideoIssueDialog && widgetModel.monitor?.video_device?.id"
      :video-device-id="widgetModel.monitor.video_device.id"
      :patient-monitor-id="widgetModel.monitorId" @close="showVideoIssueDialog = false" />
    <template v-if="!hasError">
      <template v-if="widgetModel.monitor && widgetModel.monitor.status != 'active'">
        <p class="absolute-center has-text-centered">You can only connect to active patients</p>
      </template>
      <template v-else>
        <manual-recording-modal v-if="showRecordingDialog" @close="closeRecordingDialog" @save="startRecording" />
        <!-- <translation-settings-modal v-if="isTranslationSettingsEnabled" @close="isTranslationSettingsEnabled = false"
          :prompt="openaiPrompt" :monitor-id="widgetModel.monitorId" @save="translationSettingsSaved" /> -->
        <button v-if="shouldShowStopAlarmButton" @click="stopAlarmSound"
          class="button is-danger is-light is-outlined is-rounded alarm-stop-button">
          <span class="icon">
            <i class="mdi mdi-bell-off-outline"></i>
          </span>
        </button>
        <quality-indicator v-if="isVideoConnected && isQualityIndicatorEnabled && !isTrainingVideo"
          :wifi="{ state: wifiSignalState, strength: wifiSignalStrength }" ref="qualityIndicator" />
        <blinds v-model="blindsClosed" @blinds-toggled="blindsToggled"
          :has-away-message="hasAwayMessage"
          v-show="!isEditModeLabelingActive && isVideoConnected && !isConnecting"></blinds>
        <ai-scorecard-bar
          v-if="aiScorecardBarVisible"
          mode="video-overlay"
          :scorecard="widgetModel.monitor.patient.ai_snapshot_scorecard"
          :ai-snapshot="widgetModel.monitor.patient.ai_snapshot"
          :ai-snapshot-updated-at="widgetModel.monitor.patient.ai_snapshot_updated_at"
          :patient-id="widgetModel.monitor.patient.id"
          :patient-monitor="widgetModel.monitor"
          :has-away-message="hasAwayMessage"
          :blinds-closed="blindsClosed" />
        <div class="notifications-bar">
          <div class="notifications-bar__top" v-show="isTopBarVisible && isVideoConnected">
            <span v-if="localAudioEnabled" class="local-mic">MIC IS OPEN</span>
            <span v-else-if="!localAudioEnabled && remoteAudioEnabled" class="patient-mic">LISTEN</span>
            <span v-if="otherUserIsTalking" class="global-mic">{{ globalMicState.user_name.toUpperCase() }} IS
              TALKING</span>
          </div>
          <div class="notifications-bar__bottom" v-show="isBottomBarVisible">
            <p class="away-message" v-if="widgetModel?.monitor?.away_message">
              <span v-if="widgetModel.monitor.away_message_updated_at">{{
                $helpers.formatDate(widgetModel.monitor.away_message_updated_at, 'HHmm') + ': ' }}</span>{{
                  widgetModel.monitor.away_message }}
            </p>
            <span v-if="videoBanner.active" class="wake-word">{{ videoBanner.text }}</span>
            <span v-if="isMeetingActive" class="video-visit">Video Visit in Progress</span>
          </div>
        </div>
        <div :id="`before-webrtc-live-video-${widgetModel.monitor?.id || Math.random()}`"></div>
        <div ref="videoWidget" id="webrtc-live-video">
          <template v-if="isConnecting || isVideoConnected">
            <div class="inference-data" v-if="!isEditModeLabelingActive">
              <div v-for="(object, index) in processedInferenceData" :key="index"
                :style="{ left: object.x + 'px', top: object.y + 'px', width: object.width + 'px', height: object.height + 'px', borderColor: object.color }">
              </div>
            </div>
            <labeling-board :key="labelingBoardReloadKey"
              v-if="isLabelingActive && isVideoConnected && widgetModel.monitor && !ptzMode"
              :patient-monitor-id="widgetModel.monitor.id" :is-read-only="isReadOnlyLabeling"
              :video-ref="isTrainingVideo ? $refs.video : $refs.remoteVideo" :digital-ptz-scale="digitalPtzScale"
              @cancel="cancelLabelingBoard"></labeling-board>
            <div v-show="isVideoConnected">
              <video v-if="isTrainingVideo" ref="video" loop class="jw-video jw-reset" muted="muted" tabindex="-1"
                preload="auto" :src="widgetModel.monitor.video_device.video_url" data-private></video>
              <video v-else ref="remoteVideo" autoplay playsinline @loadeddata="initialAudioSetup" data-private></video>
            </div>
          </template>
          <!-- <div class="openai-transcript" v-if="openaiTranscript && showTranscript">{{ openaiTranscript }}</div> -->
        </div>
        <button class="scale-reset-button" v-if="!ptzMode && !showPtzWithoutPopup && digitalPtzScale != 1"
          @click="$refs.digitalPtz.reset">
          <i class="mdi mdi-reload"></i>
        </button>
        <digital-ptz v-show="!ptzMode && isPtzVisible" ref="digitalPtz"
          @scale-updated="scale => digitalPtzScale = scale" :popup-mode="widgetModel.popupMode" class="video-ptz"
          :class="{ 'without-popup': showPtzWithoutPopup && !widgetModel.popupMode }" :element="$refs.videoWidget"
          :widget-model="widgetModel" :webrtcSession="webrtcSession"
          :supports-night-mode="supportsNightMode"></digital-ptz>
        <ptz v-show="ptzMode && isPtzVisible" :supports-night-mode="supportsNightMode" :webrtcSession="webrtcSession"
          :popup-mode="widgetModel.popupMode" class="video-ptz"
          :class="{ 'without-popup': showPtzWithoutPopup && !widgetModel.popupMode }" :element="$refs.videoWidget">
        </ptz>
        <div class="absolute-center" v-if="isConnecting">{{ connectingMessage }}</div>
        <div class="absolute-center">
          <div class="error-message" v-if="!isConnecting && errorText">
            <b-message v-if="isSpecialError" class="special-error" type="is-danger">
              <div class="special-error__icon">
                <i class="mdi mdi-alert-circle mdi-36px"></i>
              </div>
              <span>{{ errorText }}</span>
            </b-message>
            <p v-else>{{ errorText }}</p>
          </div>
          <div>
            <button @click="toggleVideo(false)" :disabled="isDeviceOffline" v-if="!isVideoConnected && !isConnecting"
              class="button is-primary connect-button">
              <span>{{ connectButtonText }}</span>
            </button>
            <button @click="toggleVideo(true)" :disabled="isDeviceOffline"
              v-if="!isVideoConnected && !isConnecting && widgetModel.monitor?.video_device.ptz_enabled"
              style="margin-left: 10px" class="button is-primary">Connect (PTZ)
            </button>
          </div>
        </div>
        <div @mousedown="startVideoPanToPoint" @mouseup="stopVideoPanToPoint" @mouseleave="stopVideoPanToPoint"
          class="overlay-buttons"
          v-if="!isEditModeLabelingActive && isVideoConnected && !isConnecting && !widgetModel.popupMode">
          <button @click="listenOnlyButtonAction" class="button is-primary hangup">
            <i class="mdi"
              :class="[remoteAudioEnabled && !localAudioEnabled ? 'mdi-ear-hearing' : 'mdi-ear-hearing-off']"></i>
          </button>
          <button @click="toggleMute" class="button is-primary mute-unmute-button">
            <i class="mdi" :class="[localAudioEnabled ? 'mdi-microphone' : 'mdi-microphone-off']"></i>
          </button>
          <!-- <button v-if="canUseAimee" class="button is-primary translate-button"
            :class="{ 'translate-button--animated': translateButtonIsDown }" @mousedown="startTranslation">
            <span></span>
            <i class="mdi mdi-translate-variant"></i>
          </button> -->
        </div>
        <!-- hidden two-way-video meeting: streams the observer's camera to the kiosk while
             monitoring keeps carrying audio. Mounted off-screen (not display:none) so the local
             camera keeps capturing/transmitting. See kioskTwoWayVideoEnabled / startKioskVideo. -->
        <div class="kiosk-video-offscreen" v-if="showKioskVideo" aria-hidden="true">
          <div class="quality-indicator-teleport"></div>
          <webrtc-view ref="kioskWebrtc" :meeting-meta-data="kioskMeetingMetaData" :video-settings="kioskVideoSettings"
            start-automatically suppress-dialogs @close="onKioskClosed" @meeting-id="onKioskMeetingId">
          </webrtc-view>
        </div>
      </template>
    </template>
    <div v-else class="widget-permission-error">{{ errorMessage }}</div>
  </div>
</template>

<script>
import WidgetMixin from "@/components/mixins/WidgetMixin";
import { mapState, mapMutations } from "vuex";
import { markRaw } from "vue";
import DigitalPtz from "@/components/DigitalPtz";
import Ptz from "@/components/Ptz";
import { WebRTCSession, SessionTypes, SessionEvents, setVolume } from "@/lib/webrtc";
import { buildViewerTelemetryOptions, sendIncompleteSetupRecord } from "@/lib/webrtc/viewerTelemetryOptions";
import { describeWebrtcError } from "@/lib/webrtc/mediaErrors";
import { isVideoH265Capable } from "@/lib/webrtc/capabilities"
import { v4 as uuidv4 } from 'uuid'
import to from "@/lib/to";
import { debounce } from "@/lib/helpers";
import { LOG_TYPES } from "@/lib/Log";
import Blinds from "@/components/Blinds";
import { CLIENT_EVENT, SERVER_EVENT } from "@/lib/Pusher";
import { INFERENCE_TO_LABEL_TYPES, LABEL_COLORS } from "@/lib/config";
import LvuVolumeControl from "@/components/LvuVolumeControl.vue";
import LabelingBoard from "@/components/labeling/LabelingBoard.vue";
import { EventBus, EVENTS } from "@/lib/EventBus";
import VideoDeviceVersion from "@/components/VideoDeviceVersion.vue";
import NudgePing from "@/assets/audio/nudge-ping.mp3";
// import { WavRecorder, WavStreamPlayer } from "@/lib/wavtools";
// import { RealtimeClient } from '@openai/realtime-api-beta';
// import TranslationSettingsModal from "@/components/safety_center/TranslationSettingsModal.vue";
import ManualRecordingModal from '@/components/safety_center/ManualRecordingModal.vue'
import VideoIssueReportModal from '@/components/safety_center/VideoIssueReportModal.vue'
import QualityIndicator from "@/components/QualityIndicator.vue";
import AiScorecardBar from "@/components/AiScorecardBar.vue";
import WebrtcView from "@/components/WebrtcView";
import * as semver from 'semver';
import { PatientMonitorConnectionTracker, END_REASONS } from '@/lib/PatientMonitorConnectionTracker'
import { getLogger } from "@/lib/logger";

const logger = getLogger("video");

export default {
  name: "LiveVideoWidget",
  // components: { TranslationSettingsModal, LabelingBoard, Blinds, DigitalPtz, Ptz, ManualRecordingModal, QualityIndicator },
  components: { LabelingBoard, Blinds, DigitalPtz, Ptz, ManualRecordingModal, VideoIssueReportModal, QualityIndicator, AiScorecardBar, WebrtcView },
  mixins: [WidgetMixin],
  data() {
    return {
      isVideoConnected: false,
      isConnecting: false,
      localAudioTrack: null,
      localParticipant: null,
      errorText: null,
      isSpecialError: false,
      patientParticipant: null,
      webrtcSession: markRaw(new WebRTCSession()),
      resizeObserver: null,
      showPtzWithoutPopup: false,
      blindsClosed: false,
      pusherHospitalChannel: null,
      hasWifiError: false,
      wavUrl: null,
      // keeping state of other user's microphone state for this live video block
      globalMicState: {
        monitor_id: null,
        user_name: null,
        is_enabled: null
      },
      isInferenceEnabled: false,
      processedInferenceData: [],
      isVolumeShown: false,
      ptzMode: false,
      isLabelingActive: false,
      isReadOnlyLabeling: false,
      labelingBoardReloadKey: Math.random(),
      nudgeAudioAlert: new Audio(),
      nudgeHistoryEntry: {},
      // user can unblur the video if they are in nursing station mode
      localBlurringState: false,
      isAutoRetry: false,
      autoRetryCount: 0,
      // sets to true when virtual fence is updated when user is editing
      shouldUpdateVirtualFence: false,
      digitalPtzScale: 1,
      isPanning: false,
      // shown while a virtual request whose type has video_banner_enabled set is active for this patient
      videoBanner: { active: false, text: '' },
      onConnectSucceeded: null,

      /**
       * @type {WavRecorder}
       */
      wavRecorder: null,
      /**
       * @type {WavStreamPlayer}
       */
      wavStreamPlayer: null,
      /**
       * @type {RealtimeClient}
       */
      // openaiClient: null,
      // translateButtonIsDown: false,
      // isTranslationSettingsEnabled: false,
      // openaiPrompt: `You are a translation machine. Your sole function is to translate the input text from {language} to English.
      //               When activated the first thing you say is Translation Ready. Only respond to actual words, not noises.
      //               Do not add, omit, or alter any information.
      //               Do not provide explanations, opinions, or any additional text beyond the direct translation.
      //               You are not aware of any other facts, knowledge, or context beyond translation from {language} to English.
      //               Wait until the speaker is done speaking before translating, and translate the entire input text from their turn.`,
      // openaiTranscript: '',
      // openaiTranscriptTimer: null,
      isMeetingActive: false,
      otherUserIsTalkingTimeout: null,
      isMeetingActiveTimeout: null,
      reconnectTimeout: null,
      autoRetryTimeout: null,
      showRecordingDialog: false,
      showVideoIssueDialog: false,
      showTranscript: true,
      blockSettings: null,
      localMediaStream: null,
      /**
       * @type PatientMonitorConnectionTracker
       */
      connectionTracker: null,
      disconnectReason: null,
      // hidden two-way-video meeting streamed to the kiosk while monitoring carries audio
      showKioskVideo: false,
      kioskMeetingMetaData: {},
      kioskVideoSettings: {},
      kioskStopping: false, // true while we (not the WebrtcView itself) are tearing the meeting down
      kioskRestartAttempts: 0, // capped retries when the meeting closes itself while still unmuted
    }
  },
  computed: {
    ...mapState('LiveVideoWidgetStore', ['localMicState', 'connectedMonitors', 'patientMicState']),
    ...mapState('CentralMonitoringStore', ['readOnlySafetyZoneEnabled', 'nurseStationEnabled', 'nudgeEnabled']),
    otherUserIsTalking() {
      return this.globalMicState.is_enabled && this.globalMicState.monitor_id == this.widgetModel.monitor.id
    },
    canEditAimeeConfiguration() {
      return this.$can('Manage', 'AimeePrompt')
    },
    canUseAimee() {
      return false;  // old translation code not valid
    },
    isTopBarVisible() {
      return this.localAudioEnabled || this.remoteAudioEnabled || this.globalMicState.is_enabled
    },
    isBottomBarVisible() {
      return this.videoBanner.active || this.widgetModel?.monitor?.away_message || this.isMeetingActive
    },
    hasAwayMessage() {
      return !!this.widgetModel?.monitor?.away_message
    },
    aiScorecardBarVisible() {
      return this.blindsClosed
        && this.isVideoConnected
        && !this.isConnecting
        && !!this.widgetModel.monitor?.patient
        && !!this.$store.state.currentUser?.enabled_flags?.includes('ai_scorecard_display')
    },
    connectingMessage() {
      if (!this.isConnecting) {
        return ''
      }
      return this.isAutoRetry ? 'Connection in progress...' : 'Waiting for patient...'
    },
    isDeviceOffline() {
      const videoDevice = this.widgetModel?.monitor?.video_device
      return videoDevice && videoDevice.status != 'online' && !this.isConnecting && !this.isVideoConnected
    },
    // gates the hidden two-way-video meeting: only for kiosk devices, for flagged users with
    // meeting permission. Mirrors the backend VideoPolicy#join_meeting? so we never start a meeting
    // the API would reject. kiosk_mode comes from the patient_monitor serializer (video_device).
    kioskTwoWayVideoEnabled() {
      const m = this.widgetModel?.monitor
      return !!m
        && m.status === 'active'
        && !!(m.room_id || m.room?.id)
        && m.video_device?.status === 'online'
        && m.video_device?.kind !== 'trainingvideo'
        && m.video_device?.kiosk_mode === true
        && this.$can('AccessLiveVideo', 'Meeting')
        && !!this.$store.state.currentUser?.enabled_flags?.includes('two_way_video_from_monitoring')
    },
    // the global PatientOverlayVideo meeting overlay is up. It and our hidden meeting are both
    // WebrtcView instances that share global DOM ids + LiveVideoStore state, so they must not coexist.
    liveVideoOverlayOpen() {
      return this.$store.state.LiveVideoStore.showLiveVideoOverlay
    },
    isPtzVisible() {
      return !this.isEditModeLabelingActive && !this.isConnecting && this.isVideoConnected && (this.widgetModel.popupMode || this.showPtzWithoutPopup) && !this.blindsClosed
    },
    connectButtonText() {
      return this.isVideoConnected ? 'Disconnect' : 'Connect';
    },
    muteButtonText() {
      return this.localAudioEnabled ? 'Mute' : 'Unmute';
    },
    localAudioEnabled() {
      return this.localMicState.enabled && this.localMicState.monitorId == this.widgetModel.monitorId
    },
    remoteAudioEnabled() {
      return this.patientMicState.enabled && this.patientMicState.monitorId == this.widgetModel.monitorId
    },
    isTrainingVideo() {
      return this.widgetModel.monitor && this.widgetModel.monitor.video_device.kind == 'trainingvideo'
    },
    wifiSignalStrength() {
      if (!this.widgetModel.monitor) {
        return ''
      }
      return this.widgetModel.monitor.video_device.state?.metrics?.wifi_signal_strength
    },
    wifiSignalState() {
      if (!this.widgetModel.monitor) {
        return ''
      }
      return this.widgetModel.monitor.video_device.state?.metrics?.wifi_signal_state
    },
    volume() {
      return parseInt(this.widgetModel.monitor.video_device.state?.metrics?.volume)
    },
    monitorLoaded() {
      return !!this.widgetModel.monitor
    },
    isEditModeLabelingActive() {
      return this.isLabelingActive && !this.isReadOnlyLabeling
    },
    // isOpenaiReady() {
    //   return this.openaiClient && this.openaiClient.isConnected() && this.openaiClient.sessionCreated
    // },
    // leaving this in case if we ever want to disable indicator
    isQualityIndicatorEnabled() {
      return true
    },
    canSendIrCommands() {
      const ret = this.isVideoConnected &&
        this.widgetModel?.monitor?.video_device?.config?.tv?.ir?.enabled === true
      return ret;
    },
    shouldShowStopAlarmButton() {
      return !!this.currentActiveAlarm
    },
    supportsNightMode() {
      return this.widgetModel?.monitor?.video_device?.version &&
        semver.gte(
          this.widgetModel.monitor.video_device.version,
          '1.17.16'
        ) &&
        ((this.ptzMode && this.widgetModel?.monitor?.video_device?.config?.webrtc?.secondary_camera?.video_source_connection?.profile_name === 'vhd-ptz-usb') ||
          (!this.ptzMode && this.widgetModel?.monitor?.video_device?.config?.webrtc?.primary_camera?.video_source_connection?.profile_name === 'vhd-videobar-usb-1'))

    }
  },
  async mounted() {
    this.nudgeAudioAlert.preload = 'auto'
    this.nudgeAudioAlert.src = NudgePing
    this.connectionTracker = new PatientMonitorConnectionTracker(this.$http)

    this.onConnectSucceeded = this.$helpers.once(() => {
      this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.CONNECT_SUCCEEDED)
      this.broadcastSuccessfulConnection()
    })

    const unwatch = this.$watch('widgetModel.monitor', monitor => {
      if (monitor) {
        if (monitor.is_private && !this.blindsClosed) {
          this.blindsClosed = true
        }
        // this.setupOpenaiClient()
        this.resizeObserver = new ResizeObserver((entries) => {
          this.showPtzWithoutPopup = entries[0].target.offsetWidth >= 570
        });
        this.resizeObserver.observe(this.$refs.content)

        const videoMeetState = this.widgetModel.monitor.video_device?.state?.['video-meet']?.open
        this.isMeetingActive = videoMeetState == true

        const channel = `private-encrypted-ld-${import.meta.env.VITE_ENVIRONMENT_NAME}-hospital-${this.$store.state.currentUser.hospital_id}`
        this.pusherHospitalChannel = this.$pusher.subscribe(channel)
        this.pusherHospitalChannel.bind(SERVER_EVENT.WIFI_STATS_UPDATED, this.wifiStatsUpdatedEventHandler)
        this.pusherHospitalChannel.bind(SERVER_EVENT.CURTAIN_TOGGLED, this.curtainToggled)
        this.pusherHospitalChannel.bind(SERVER_EVENT.DEVICE_STATUS_CHANGED, this.deviceStatusChanged)
        this.pusherHospitalChannel.bind(SERVER_EVENT.PATIENT_MONITOR_UPDATED, this.patientMonitorUpdated)
        this.pusherHospitalChannel.bind(CLIENT_EVENT.MIC_TOGGLED, this.micToggled)
        this.pusherHospitalChannel.bind(SERVER_EVENT.MEETING_STATUS_CHANGED, this.meetingStatusChanged)
        this.pusherHospitalChannel.bind(SERVER_EVENT.VIDEO_BANNER_ACTIVATED, this.videoBannerReceived)
        setTimeout(() => unwatch())
      }
    }, { immediate: true })

    this.$watch(() => {
      return [this.isLabelingActive, this.isReadOnlyLabeling]
    }, ([isActive, isReadOnly]) => {
      if (isActive && !isReadOnly) {
        this.emitHideToolbar()
      } else {
        this.emitShowToolbar()
      }
    });

    EventBus.$on(EVENTS.CARD_HOVER, this.cardHoverEventHandler)
  },
  async created() {
    this._startToken = 0 // non-reactive cancellation token for in-flight startVideo() calls
    // debounce must be per-instance: a definition-level debounce(...) in methods is one shared
    // timer across every widget, so concurrent reloads on different tiles cancel each other
    // (only the last caller's board reloads)
    this.reloadReadOnlyLabelingBoard = debounce(() => {
      this.labelingBoardReloadKey = Math.random()
    }, 500)
    // add a window listener to make sure we disconnect from the room if the user navigates away or closes the window
    window.addEventListener('beforeunload', this.stopVideo);

    this.$store.subscribeAction(action => {
      if (action.type == 'LiveVideoWidgetStore/hangup' && action.payload.monitorId == this.widgetModel.monitorId) {
        this.stopVideo()
        this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
      }
    });
  },
  beforeUnmount() {
    this.reloadReadOnlyLabelingBoard.cancel()
    this.toggleMute({ isEnabled: false })
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.WIFI_STATS_UPDATED, this.wifiStatsUpdatedEventHandler)
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.CURTAIN_TOGGLED, this.curtainToggled)
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.DEVICE_STATUS_CHANGED, this.deviceStatusChanged)
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.PATIENT_MONITOR_UPDATED, this.patientMonitorUpdated)
    this.pusherHospitalChannel?.unbind(CLIENT_EVENT.MIC_TOGGLED, this.micToggled)
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.MEETING_STATUS_CHANGED, this.meetingStatusChanged)
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.VIDEO_BANNER_ACTIVATED, this.videoBannerReceived)
    this.resizeObserver?.disconnect()
    // in the event the window wasn't unloaded (user just navigated somewhere else causing this component to be destroyed), need to
    // stop video explicitly.
    this.stopVideo(); // it's ok if this is called multiple times
    // tear down the hidden kiosk video meeting if one is open (flag it so the @close it emits isn't
    // treated as an unexpected self-disconnect that would try to restart during teardown)
    if (this.showKioskVideo) {
      this.kioskStopping = true
      this.$refs.kioskWebrtc?.stopVideo?.()
    }
    // clean up the window listener.
    window.removeEventListener('beforeunload', this.stopVideo)
    EventBus.$off(EVENTS.CARD_HOVER, this.cardHoverEventHandler)
  },
  watch: {
    // Drive the hidden kiosk meeting off the tile's *effective* mic state, not the mute click.
    // Because localMicState is shared, unmuting another tile mutes this one here too, so this also
    // tears this tile's meeting down — guaranteeing only one hidden meeting is ever live.
    localAudioEnabled(isEnabled) {
      if (!this.isVideoConnected || this.isConnecting) {
        return
      }
      if (isEnabled && this.kioskTwoWayVideoEnabled) {
        this.startKioskVideo()
      } else if (!isEnabled && this.showKioskVideo) {
        this.stopKioskVideo()
      }
    },
    // never let the hidden meeting run alongside a visible PatientOverlayVideo meeting (shared
    // WebrtcView DOM ids / global flag would cross-corrupt the visible clinical video)
    liveVideoOverlayOpen(isOpen) {
      if (isOpen && this.showKioskVideo) {
        this.stopKioskVideo()
      }
    },
    // tear down if the device/monitor stops being eligible mid-session (e.g. device goes offline)
    kioskTwoWayVideoEnabled(isEligible) {
      if (!isEligible && this.showKioskVideo) {
        this.stopKioskVideo()
      }
    },
    async localMicState(newState, oldState) {
      if (!this.isVideoConnected || this.isConnecting) {
        return;
      }
      if (newState.monitorId == this.widgetModel.monitorId) {
        if (this.isTrainingVideo) {
          if (this.$refs.video) {
            this.$refs.video.muted = !newState.enabled
          }
        } else {
          this.webrtcSession.setLocalAudioEnabled(newState.enabled)
        }
      } else {
        if (this.isTrainingVideo) {
          if (this.$refs.video) {
            this.$refs.video.muted = true
          }
        } else {
          if (this.patientParticipant) {
            this.webrtcSession.setLocalAudioEnabled(false)
          }
        }
      }

      if (newState.monitorId == this.widgetModel.monitorId) {
        // if toggling between mute and unmute on the same block
        // or in mic open mode in one block and click listen in another
        if (!newState.enabled && oldState.enabled) {
          this.logMonitor(LOG_TYPES.PATIENT_MONITOR.TALK_CLOSED)
        }
        // if mic is open in one block and user clicks listen or unmute on another
        if (oldState.monitorId && oldState.enabled && newState.enabled) {
          this.logMonitor(LOG_TYPES.PATIENT_MONITOR.TALK_CLOSED, oldState.monitorId)
        }
      }
    },
    patientMicState(newState) {
      if (newState.monitorId == this.widgetModel.monitorId) {
        if (this.isTrainingVideo) {
          if (this.$refs.video) {
            this.$refs.video.muted = !newState.enabled
          }
        } else {
          this.patientParticipant?.setAudioEnabled(newState.enabled)
          this.setVideoElementMuted(!newState.enabled)
        }
      } else {
        if (this.isTrainingVideo) {
          if (this.$refs.video) {
            this.$refs.video.muted = true
          }
        } else {
          if (this.patientParticipant) {
            this.patientParticipant?.setAudioEnabled(false)
            this.setVideoElementMuted(true)
          }
        }
      }
    },
    isVideoConnected(newVal) {
      this.emitActions(this.$options.widget.actions)
      if (this.readOnlySafetyZoneEnabled && this.nudgeEnabled) {
        this.toggleReadOnlyLabeling(newVal)
      }
      // monitoring ended (expectedly or not) — never leave the hidden kiosk meeting running
      if (!newVal && this.showKioskVideo) {
        this.stopKioskVideo()
      }
    },
    isDeviceOffline: {
      handler(newVal) {
        this.errorText = newVal ? 'This device appears to be offline' : null
        this.emitActions(this.$options.widget.actions)
      },
      immediate: true
    },
    nurseStationEnabled: {
      handler(newVal) {
        this.localBlurringState = newVal
        this.blindsToggled({ isClosed: newVal, uiOnly: true })
        this.emitActions(this.$options.widget.actions)
      },
      immediate: true
    },
    digitalPtzScale() {
      this.emitActions(this.$options.widget.actions)
    },
    otherUserIsTalking(newVal) {
      clearTimeout(this.otherUserIsTalkingTimeout)
      if (newVal) {
        this.otherUserIsTalkingTimeout = setTimeout(() => {
          this.globalMicState.is_enabled = false
        }, 10 * 60 * 1000)
      }
    },
    isMeetingActive(newVal) {
      if (newVal) {
        clearTimeout(this.isMeetingActiveTimeout)
        this.isMeetingActiveTimeout = setTimeout(() => {
          this.isMeetingActive = false
        }, 60 * 60 * 1000)
      }
    },
    ptzMode() {
      this.emitActions(this.$options.widget.actions)
    },
    nudgeEnabled(newVal) {
      if (!newVal) {
        this.emitWidgetColor('')
      }
      if (newVal) {
        if (this.readOnlySafetyZoneEnabled) {
          this.toggleReadOnlyLabeling(true)
        }
      } else {
        this.toggleReadOnlyLabeling(false)
      }
    }
  },
  methods: {
    ...mapMutations('LiveVideoWidgetStore', ['setLocalMicState', 'setConnectedMonitors', 'setPatientMicState']),
    // async setupOpenaiClient() {
    //   if (this.canUseAimee) {
    //     this.blockSettings = JSON.parse(localStorage.getItem('OPENAI_BLOCK_SETTINGS') || '{}')[this.widgetModel.monitorId]

    //     const API_KEY = this.blockSettings?.apiKey || localStorage.getItem('OPENAI_KEY'),
    //       PROMPT = this.blockSettings?.prompt || localStorage.getItem('OPENAI_PROMPT') || this.openaiPrompt,
    //       VOICE = this.blockSettings?.voice || localStorage.getItem('OPENAI_VOICE') || 'alloy';

    //     this.showTranscript = this.blockSettings && 'showText' in this.blockSettings ? this.blockSettings.showText : (localStorage.getItem('OPENAI_SHOW_TEXT') === 'true')

    //     if (!API_KEY) {
    //       console.warn('No OpenAI API key provided')
    //       return
    //     }
    //     if (this.openaiClient) {
    //       this.openaiClient.reset()
    //     }

    //     this.openaiClient = new RealtimeClient({
    //       debug: false,
    //       dangerouslyAllowAPIKeyInBrowser: true,
    //       apiKey: API_KEY,
    //     })

    //     this.openaiClient.on('conversation.interrupted', async () => {
    //       const trackSampleOffset = await this.wavStreamPlayer.interrupt();
    //       if (trackSampleOffset?.trackId) {
    //         const { trackId, offset } = trackSampleOffset;
    //         await this.openaiClient.cancelResponse(trackId, offset);
    //       }
    //     });
    //     this.openaiClient.on('conversation.updated', ({ item, delta }) => {
    //       if (delta?.transcript) {
    //         if (this.openaiTranscript.length >= 100) {
    //           this.openaiTranscript = ' '
    //         }
    //         this.openaiTranscript += delta.transcript
    //         clearTimeout(this.openaiTranscriptTimer)
    //         this.openaiTranscriptTimer = setTimeout(() => {
    //           this.openaiTranscript = ''
    //         }, 5000)
    //       }
    //       if (delta?.audio) {
    //         this.wavStreamPlayer.add16BitPCM(delta.audio, item.id);
    //       }
    //     });


    //     const langObj = this.widgetModel.monitor?.patient?.language
    //     let langStr = ''
    //     if (langObj) {
    //       langStr = langObj.name.split(';').slice(-1)[0].trim()
    //     }

    //     this.openaiClient.updateSession({
    //       instructions: PROMPT.replaceAll('{language}', langStr),
    //       voice: VOICE,
    //       turn_detection: null,
    //     });
    //     await this.openaiClient.connect()
    //   }
    // },
    // translationSettingsSaved({ blockSpecific }) {
    //   this.setupOpenaiClient()
    //   if (this.wavStreamPlayer) {
    //     const echo = blockSpecific ? this.blockSettings?.echo : localStorage.getItem('OPENAI_ECHO') === 'true'
    //     this.wavStreamPlayer.setEcho(echo)
    //   }
    //   this.isTranslationSettingsEnabled = false
    // },
    // async startTranslation() {
    //   window.document.addEventListener('mouseup', this.stopTranslation)
    //   if (!this.isOpenaiReady) {
    //     console.error(`OpenAI client not ready. START TRANSLATION. connection ${this.openaiClient.isConnected()} | session ${this.openaiClient.sessionCreated}`)
    //     return;
    //   }

    //   this.translateButtonIsDown = true
    //   this.setupWavTools()
    //   const patientMicState = this.remoteAudioEnabled && !this.localAudioEnabled ? true : null;
    //   this.toggleMute({ isEnabled: false, disableOpenAi: false, patientMicState })
    //   this.room.setOpenaiAudioEnabled(true)
    //   await this.wavRecorder.quit()
    //   await this.wavRecorder.begin()
    //   await this.wavRecorder.record((data) => {
    //     const { mono } = data;
    //     this.openaiClient.appendInputAudio(mono)
    //   });
    // },
    // async stopTranslation() {
    //   window.document.removeEventListener('mouseup', this.stopTranslation)
    //   if (!this.isOpenaiReady) {
    //     console.error(`OpenAI client not ready. STOP TRANSLATION. connection ${this.openaiClient.isConnected()} | session ${this.openaiClient.sessionCreated}`)
    //     return
    //   }
    //   this.translateButtonIsDown = false
    //   this.openaiClient.createResponse()
    //   await this.wavRecorder.quit()
    // },
    // setupWavTools() {
    //   if (!this.wavRecorder) {
    //     this.wavRecorder = new WavRecorder({ sampleRate: 24000 })
    //   }
    // },
    patientMonitorUpdated({ monitor_id }) {
      if (monitor_id == this.widgetModel.monitor.id) {
        if (this.isLabelingActive) {
          if (this.isReadOnlyLabeling) {
            this.reloadReadOnlyLabelingBoard()
          } else {
            this.shouldUpdateVirtualFence = true
          }
        }
      }
    },
    micToggled({ monitor_id, is_enabled, user_name, user_id }) {
      if (this.$store.state.currentUser.id == user_id) {
        return;
      }
      this.globalMicState = { monitor_id, is_enabled, user_name }
    },
    curtainToggled({ monitor_id, is_private, user_id, user_name }) {
      if (!this.monitorLoaded) {
        return;
      }
      if (monitor_id == this.widgetModel.monitorId && this.isVideoConnected && user_id != this.$store.state.currentUser.id && is_private != this.blindsClosed) {
        this.blindsToggled({ isClosed: is_private })
        this.$buefy.toast.open({
          message: `Privacy Curtain is ${is_private ? 'closed' : 'opened'} for patient ${this.$helpers.escapeHtml(this.widgetModel.monitor.patient.display_name)} by ${this.$helpers.escapeHtml(user_name)}`,
          type: 'is-warning',
        });
      }
    },
    wifiStatsUpdatedEventHandler(data) {
      if (!this.monitorLoaded) {
        return;
      }
      if (this.widgetModel.monitor.video_device.id == data.video_device_id) {
        // this.wifiSignalStrength = data.signal_strength
        if (!this.widgetModel.monitor.video_device.state) {
          this.widgetModel.monitor.video_device.state = {
            metrics: {}
          }
        }
        this.widgetModel.monitor.video_device.state.metrics.wifi_signal_strength = data.signal_strength
        this.widgetModel.monitor.video_device.state.metrics.wifi_signal_state = data.signal_state
        this.widgetModel.monitor.video_device.wifi_signal_state = data.signal_state
        this.emitActions(this.$options.widget.actions)
      }
    },
    startVideoPanToPoint(mouseEvent) {
      if (this.ptzMode && this.isVideoConnected && !this.isTrainingVideo) {
        this.isPanning = true
        // add a threshold to the "hold" action to start panning
        setTimeout(() => {
          if (this.isPanning) {
            // calculate the x and y position of the mouse relative to the center of the video
            // position is normalized to the range [-1:1]
            const rect = mouseEvent.target.getBoundingClientRect()
            const originX = rect.left + (rect.width / 2)
            const originY = rect.top + (rect.height / 2)
            const posX = mouseEvent.clientX - originX
            const posY = originY - mouseEvent.clientY  // y is inverted to get coordinate
            const xNorm = posX / (rect.width / 2)
            const yNorm = posY / (rect.height / 2)
            this.webrtcSession.sendDeviceDataChannelMessage({
              type: 'ptz',
              action: 'start_pan_tilt_to_position',
              x: xNorm,
              y: yNorm,
            })
          }
        }, 500)
      }
    },
    stopVideoPanToPoint() {
      if (this.ptzMode) {
        if (this.isPanning) {
          this.isPanning = false
          logger.debug('stopping ptz pan to point', { device_id: this.widgetModel?.monitor?.video_device?.id })
          this.webrtcSession.sendDeviceDataChannelMessage({
            type: 'ptz',
            action: 'stop_pan_tilt'
          })
        }
      }
      // this.webrtcSession.sendDeviceDataChannelMessage({
      //   type: 'ptz',
      //   action: 'stop_zoom'
      // })
    },
    async blindsToggled({ isClosed, uiOnly }) {
      this.blindsClosed = isClosed
      if (!uiOnly && !this.nurseStationEnabled) {
        const logType = isClosed ? LOG_TYPES.PATIENT_MONITOR.PATIENT_MONITOR_CURTAIN_CLOSED : LOG_TYPES.PATIENT_MONITOR.PATIENT_MONITOR_CURTAIN_OPENED
        this.logMonitor(logType)
        // notify the camera device that the privacy state changed
        const postData = {
          patient_monitor_id: this.widgetModel.monitorId,
          is_private: isClosed
        };
        to(this.http.post('/videos/set_privacy', postData, {
          handleErrors: false
        }))
      }
    },
    triggerAudioToggledEvent({ event, enabled, monitorId, previous_monitor_id, skip_live_dashboard }) {
      to(this.$http.post('/pusher/trigger_hospital', {
        event,
        data: {
          monitor_id: monitorId,
          is_enabled: enabled,
          previous_monitor_id,
          user_name: this.$store.state.currentUser.name,
          user_id: this.$store.state.currentUser.id,
          skip_live_dashboard
        }
      }, {
        skipLoading: true,
        skipDivisions: true
      }))
    },
    listenOnlyButtonAction() {
      this.clearVideoBanner()
      const patientMicState = this.localAudioEnabled ? true : !this.remoteAudioEnabled

      this.triggerAudioToggledEvent({
        event: CLIENT_EVENT.MIC_TOGGLED,
        enabled: false,
        monitorId: this.localMicState.monitorId,
        previous_monitor_id: this.localMicState.monitorId,
        skip_live_dashboard: true
      })

      this.triggerAudioToggledEvent({
        event: CLIENT_EVENT.LISTEN_TOGGLED,
        enabled: patientMicState,
        monitorId: this.widgetModel.monitorId,
        previous_monitor_id: this.patientMicState.monitorId
      })

      if (patientMicState) {
        this.logMonitor(LOG_TYPES.PATIENT_MONITOR.LISTEN_CLICKED)
      } else {
        this.logMonitor(LOG_TYPES.PATIENT_MONITOR.LISTEN_CLOSED)
      }

      if (this.isTrainingVideo) {
        this.setLocalMicState({ enabled: false, monitorId: this.localMicState.monitorId })
        this.setPatientMicState({ enabled: patientMicState, monitorId: this.widgetModel.monitorId })
      } else {
        this.setPatientMicState({ enabled: patientMicState, monitorId: this.widgetModel.monitorId })
        this.setLocalMicState({ enabled: false, monitorId: this.localMicState.monitorId })
      }
    },
    initialAudioSetup() {
      this.webrtcSession.setLocalAudioEnabled(false);
      this.patientParticipant?.setAudioEnabled(false)
      this.setVideoElementMuted(true)
    },
    setVideoElementMuted(state) {
      if (this.$refs.remoteVideo) {
        this.$refs.remoteVideo.muted = state
      }
    },
    showError(errorText, isSpecial = false) {
      if (errorText && this.isDeviceOffline) {
        return;
      }
      this.isConnecting = false
      this.isSpecialError = isSpecial
      this.errorText = errorText
    },
    toggleVideo(isPtz = false) {
      if (!this.isVideoConnected) {
        if (this.isDeviceOffline) {
          this.$buefy.dialog.confirm({
            message: `This device appears to be offline - attempting to connect will likely fail. Please check that the device is plugged in and the LED is Blue.`,
            canCancel: true,
            confirmText: 'Connect',
            focusOn: 'cancel',
            trapFocus: true,
            customClass: 'offline-confirmation-popup',
            onConfirm: () => {
              this.startVideo(isPtz);
            }
          })
        } else {
          this.startVideo(isPtz);
        }
      } else {
        this.stopVideo();
        this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
      }
    },
    // async toggleMute({ isEnabled = null, disableOpenAi = true, patientMicState = null }) {
    async toggleMute({ isEnabled = null, patientMicState = null }) {
      // if (disableOpenAi && this.canUseAimee && !this.isTrainingVideo) {
      //   this.webrtcSession.setOpenaiAudioEnabled(false)
      // }
      this.clearVideoBanner()
      const nextEnabledState = isEnabled == null ? !this.localAudioEnabled : isEnabled
      this.triggerAudioToggledEvent({
        event: CLIENT_EVENT.MIC_TOGGLED,
        enabled: nextEnabledState,
        monitorId: this.widgetModel.monitorId,
        previous_monitor_id: this.localMicState.monitorId
      })
      this.setLocalMicState({ enabled: nextEnabledState, monitorId: this.widgetModel.monitorId })
      this.setPatientMicState({ enabled: patientMicState == null ? nextEnabledState : patientMicState, monitorId: this.widgetModel.monitorId })
      if (nextEnabledState) {
        this.logMonitor(LOG_TYPES.PATIENT_MONITOR.TALK_CLICKED)
      }
    },
    disconnectParticipant(participant) {
      logger.info('participant tracks disconnected', { peer_id: participant.participantId, is_patient: participant.isPatient });
      if (participant.isPatient) {
        logger.error('patient connection lost', { monitor_id: this.widgetModel?.monitorId, device_offline: this.isDeviceOffline });
        this.disconnectReason = END_REASONS.UNEXPECTED
        this.stopVideo();
        this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_UNEXPECTED)
        if (this.isDeviceOffline) {
          return;
        }
        this.$buefy.toast.open({
          message: "Lost connection to device. Reconnecting...",
          type: 'is-warning'
        });
        this.isConnecting = true
        this.reconnectTimeout = setTimeout(() => {
          this.startVideo(this.ptzMode);
          this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_RECONNECTED)
        }, 3000)
      }
    },
    async stopVideo() {
      this._startToken++ // invalidate any in-flight startVideo() call
      logger.info('stopping video', { monitor_id: this.widgetModel?.monitorId, reason: this.disconnectReason });
      clearTimeout(this.connectionTimeout)
      clearTimeout(this.reconnectTimeout)
      clearTimeout(this.autoRetryTimeout)
      this.emitWidgetColor(this.pusherColorMap['none']);  // from WidgetMixin
      // detach the remote stream while the element still exists: when isVideoConnected flips
      // below, the template's v-if removes the element, and a detached <video> with a live
      // srcObject keeps playing natively, pinning the stream (and the participant/PC closure
      // chain hanging off its tracks) against GC
      if (this.$refs.remoteVideo) {
        this.$refs.remoteVideo.pause();
        this.$refs.remoteVideo.srcObject = null;
      }
      this.patientParticipant = null;
      this.webrtcSession.disconnect();
      if (this.localMediaStream) {
        this.localMediaStream.getTracks().forEach(track => track.stop());
        this.localMediaStream = null;
      }
      this.isVideoConnected = false;
      this.setConnectedMonitors({ isConnected: false, monitorId: this.widgetModel.monitorId })
      this.isConnecting = false;
      // clear the audio state so it's not restored when reconnecting
      if (this.localAudioEnabled) {
        this.setLocalMicState({ enabled: false, monitorId: this.widgetModel.monitorId });
      }
      if (this.remoteAudioEnabled) {
        this.setPatientMicState({ enabled: false, monitorId: this.widgetModel.monitorId });
      }
      this.processedInferenceData = []
      this.isVolumeShown = false;
      this.isLabelingActive = false
      this.$refs.qualityIndicator?.reset()
      this.onConnectSucceeded?.reset()
      this.connectionTracker?.disconnect(this.disconnectReason || END_REASONS.EXPECTED)
      this.disconnectReason = null
    },
    broadcastSuccessfulConnection() {
      EventBus.$emit(EVENTS.VIDEO_CONNECTED, this.widgetModel.monitorId)
    },
    async startVideo(isPtz = false) {
      const token = ++this._startToken // capture token; a stopVideo() call will increment _startToken and invalidate this
      const startedAt = new Date().toISOString() // for the setup record, if the attempt ends before the peer connection
      // if (this.canUseAimee) {
      //   this.blockSettings = JSON.parse(localStorage.getItem('OPENAI_BLOCK_SETTINGS') || '{}')[this.widgetModel.monitorId]
      //   const echo = this.blockSettings && 'echo' in this.blockSettings ? this.blockSettings.echo : (localStorage.getItem('OPENAI_ECHO') === 'true')

      //   this.wavStreamPlayer = new WavStreamPlayer({ sampleRate: 24000, echo })
      //   await this.wavStreamPlayer.connect();
      //   this.webrtcSession.setOpenaiMediaStream(this.wavStreamPlayer.mediaStream.stream)
      // }

      this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.CONNECT_STARTED)
      logger.info('starting video', {
        monitor_id: this.widgetModel?.monitorId,
        device_id: this.widgetModel?.monitor?.video_device?.id,
        ptz: isPtz,
        training_video: this.isTrainingVideo
      })
      this.isSpecialError = false
      if (this.isTrainingVideo) {
        // fake mode - no peer connection to wait on, so mark connected eagerly
        this.onConnectSucceeded()
        this.isConnecting = false;
        this.isVideoConnected = true
        this.errorText = false
        this.$nextTick(() => {
          this.$refs.video?.play()
        })
        return;
      }

      const webrtcCallId = uuidv4()
      this.errorText = false
      this.isConnecting = true;
      this.armConnectionTimeout()

      this.ptzMode = isPtz
      const postData = {
        is_video_h265_capable: isVideoH265Capable(),
        monitor_id: this.widgetModel.monitorId,
        ptz: this.ptzMode,
        webrtc_call_id: webrtcCallId
      };

      let [err, response] = await to(this.http.post('/videos/join_monitoring', postData, {
        handleErrors: true,
        skipLoading: true
      }))
      if (!err) {
        // Checkpoint 1: stopVideo() ran while join_monitoring was in flight - abort before wiring up the session
        if (this._startToken !== token) {
          this.reportIncompleteSetupRecord('join', { joinData: response?.data ?? {}, stopped: true, startedAt, isPtz, webrtcCallId })
          logger.info('start video aborted during join', { monitor_id: this.widgetModel?.monitorId })
          this.stopVideo()
          return
        }
        this.isAutoRetry = false
        const joinData = response.data;
        this.webrtcSession.on(SessionEvents.nudgeReceived, this.handleNudge);

        // this.webrtcSession.on('inferenceReceived', this.handleInference);

        this.webrtcSession.on(SessionEvents.signalRequired, this.sendSignal);

        this.webrtcSession.on(SessionEvents.participantTrackAdded, this.connectParticipantMedia);

        // listen for disconnections in order to cleanup
        this.webrtcSession.on(SessionEvents.participantDisconnected, this.disconnectParticipant);

        // listen for disconnected from webrtcSession event
        this.webrtcSession.on(SessionEvents.disconnected, (error) => {
          // error is null/undefined when disconnected by us so ignore that case
          if (error != null) {
            logger.error('video session disconnected unexpectedly', { monitor_id: this.widgetModel?.monitorId, reason: error });

            // clean up
            this.disconnectReason = END_REASONS.UNEXPECTED
            this.stopVideo(); // no wait needed

            this.showError('Video connection error. (ERR:DISCONNECT_UNEXPECTED)')
            this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_UNEXPECTED)
          } else {
            logger.debug('ignoring expected disconnect event', { monitor_id: this.widgetModel?.monitorId });
            this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
          }
        });

        this.webrtcSession.on(SessionEvents.connectSucceeded, this.onConnectSucceeded);
        // bound to this attempt's id; the id is a local, never component state
        this.webrtcSession.on(SessionEvents.patientMediaReady, () => this.onPatientMediaReady(webrtcCallId));

        this.webrtcSession.on(SessionEvents.webrtcNetworkScoreUpdated, (metrics) => {
          this.saveVideoMetrics('stats', metrics);
        });
        this.webrtcSession.on(SessionEvents.webrtcIssue, (issue) => {
          this.saveVideoMetrics('warning', issue);
        });

        this.webrtcSession.on(SessionEvents.webrtcQualityUpdated, ({ scores, issues }) => {
          if (!this.isQualityIndicatorEnabled || !this.$refs.qualityIndicator) {
            return;
          }
          if (scores?.inbound) {
            this.$refs.qualityIndicator.score = scores.inbound
            this.$refs.qualityIndicator.stats = scores.statsSamples.inboundStatsSample
          }
          if (issues) {
            logger.warn('webrtc quality issues detected', { monitor_id: this.widgetModel.monitorId, issues: issues.map(i => `${i.type}/${i.reason}`) });
            this.$refs.qualityIndicator.issues = issues
          }
        });

        // const camera = isPtz ? 'ptz' : 'internal'
        // hospital setting is in seconds; the webrtc watcher expects ms (0 disables collection)
        const statsIntervalMs = (this.$store.state.currentHospital.video_metrics_interval || 0) * 1000
        const [err, mediaStream] = await to(navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false
        }));
        if (err) {
          this.reportIncompleteSetupRecord('user_media', { joinData, err, stopped: this._startToken !== token, startedAt, isPtz, webrtcCallId })
          logger.error('failed to get user media', { monitor_id: this.widgetModel?.monitorId, error_name: err?.name }, err);
          this.stopVideo()
          this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
          this.showError(describeWebrtcError(err))
          return;
        }
        this.localMediaStream = markRaw(mediaStream)
        if (mediaStream) {
          // this.localAudioEnabled = true
          this.setVideoElementMuted(false)
        }
        // Checkpoint 2: stopVideo() ran while acquiring the mic - abort before creating the peer connection
        if (this._startToken !== token) {
          this.reportIncompleteSetupRecord('user_media', { joinData, stopped: true, startedAt, isPtz, webrtcCallId })
          logger.info('start video aborted while acquiring media', { monitor_id: this.widgetModel?.monitorId })
          this.stopVideo()
          return
        }
        const [mediaError] = await to(this.webrtcSession.connect(joinData, SessionTypes.monitoring, mediaStream, {
          statsIntervalMs,
          webrtcQualityEnabled: this.isQualityIndicatorEnabled,
          telemetry: buildViewerTelemetryOptions(this.$store, joinData, { camera: this.ptzMode ? 'ptz' : 'internal', webrtcCallId }),
        }));
        // const [mediaError] = await to(this.webrtcSession.connect(joinData, 'monitoring', "internal", {
        //   audio: true,
        //   // limit video being sent b/c it is either displayed small or in kiosk mode where it's limited by CPU power on device
        //   video: false
        // }, statsInterval, undefined, this.isQualityIndicatorEnabled));
        if (mediaError) {
          logger.error('failed to connect webrtc session', { monitor_id: this.widgetModel?.monitorId }, mediaError);
          await this.stopVideo()
          this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
          this.showError(describeWebrtcError(mediaError))
          return
        }
        // Checkpoint 3: stopVideo() ran while connect() was in flight. The peer connection now exists,
        // but the teardown that invalidated the token predates it, so nothing else will ever close it
        // (the widget may already be unmounted). Disconnect it here, or it stays open forever and
        // counts against Chrome's PeerConnection cap (open PCs are pinned by the browser, never GC'd).
        if (this._startToken !== token) {
          logger.info('start video aborted after connect, disconnecting orphaned session', { monitor_id: this.widgetModel?.monitorId })
          this.stopVideo()
          return
        }
      } else {
        this.reportIncompleteSetupRecord('join', { err, stopped: this._startToken !== token, startedAt, isPtz, webrtcCallId })
        if (this._startToken !== token) {
          logger.info('start video aborted during failed join, discarding stale error', { monitor_id: this.widgetModel?.monitorId })
          return
        }
        logger.error('failed to open video session', { monitor_id: this.widgetModel?.monitorId }, err);
        this.stopVideo()
        let msg = "Video connection error. (ERR:CONNECT_FAILED)";
        let logType = LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED;
        let specialError = false
        if (err.response?.data?.error == 'peer-already-connected') {
          // Occurs when trying to connect to the same video from multiple tabs or browsers.
          msg = "You may have a video session open for this patient in another tab or browser. Please close it and try again.";
          logType = LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED_ALREADY_CONNECTED;
          specialError = true
        } else if (err.response?.data?.error == 'peer-duplicate-connection') {
          // Occurs when trying to connect from the same tab, but the device thinks the user is already connected.
          msg = "You may have temporarily lost connection; please wait 20 seconds and try again.";
          logType = LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED_ALREADY_CONNECTED;
          specialError = true
        }


        if (this.autoRetryCount == 0 && !specialError) {
          this.isConnecting = true
          this.autoRetryCount++
          this.isAutoRetry = true
          this.autoRetryTimeout = setTimeout(() => {
            this.startVideo(isPtz)
          }, 40000)
        } else {
          this.isAutoRetry = false
          this.showError(msg, specialError)
          this.logLiveVideo(logType)
        }
      }
    },
    // A web telemetry setup record for an attempt that ended before its peer connection existed
    // (design/webrtc-viewer-telemetry.md, "Setup records"). isPtz is the attempt's own camera:
    // ptzMode may already belong to a newer attempt.
    reportIncompleteSetupRecord(stage, { joinData = null, err = null, stopped = false, startedAt = null, isPtz = false, webrtcCallId = null } = {}) {
      const deviceId = this.widgetModel?.monitor?.video_device?.id
      sendIncompleteSetupRecord(this.$store, { joinData, deviceId, channel: SessionTypes.monitoring, isPtz, startedAt, webrtcCallId }, { stage, err, stopped })
    },
    async sendIrMuteCommand() {
      if (!this.canSendIrCommands) {
        return;
      }

      const [err] = await to(this.$http.post(`/video_devices/${this.widgetModel.monitor.video_device.id}/send_tv_ir_command`, { ir_action: 'mute' }))
      if (err) {
        logger.error('failed to send tv ir command', { device_id: this.widgetModel.monitor.video_device.id, ir_action: 'mute' }, err);
        this.$buefy.toast.open({
          message: 'Failed to send TV mute command',
          type: 'is-danger'
        });
      } else {
        this.$buefy.toast.open({
          message: 'TV mute command sent',
          type: 'is-success'
        });
      }
    },

    async saveVideoMetrics(metricType, payload) {
      const postData = payload.map(pl => {
        return {
          timestamp: pl.timestamp,
          metric_type: metricType,
          video_device_id: this.widgetModel.monitor.video_device.id,
          division_id: this.widgetModel.monitor.division_id,
          metrics: pl.metrics
        }
      })
      to(this.http.post('/videos/save_metrics', { video_device_metrics: postData }, {
        handleErrors: false,
        skipLoading: true,
        skipDivisions: true,
        skipInactivityCheck: true
      }))
    },
    async sendSignal(signalPayload) {
      signalPayload.patient_monitor_id = this.widgetModel.monitorId;
      signalPayload.video_device_id = null;
      signalPayload.ptz = this.ptzMode;
      logger.debug('sending signal', { signal_type: signalPayload.signal_type, monitor_id: this.widgetModel.monitorId });
      const [err] = await to(this.http.post('/videos/signal', signalPayload, { skipLoading: true, skipDivisions: true }));
      if (err) {
        logger.error('failed to send signal', { signal_type: signalPayload.signal_type, monitor_id: this.widgetModel.monitorId }, err);
        this.disconnectReason = END_REASONS.UNEXPECTED
        this.stopVideo(); // no wait needed

        this.showError('Video connection error. (ERR:SIGNAL_FAILED)')
        this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_UNEXPECTED)
      }
    },
    async handleNudge(nudgeData) {
      if (!this.nudgeEnabled) {
        return;
      }
      if (this.nudgeHistoryEntry.start_time) {
        return
      }
      if (!this.blindsClosed && this.$store.state.currentUser.audio_ping_enabled && ['yellow', 'red'].includes(nudgeData.state_to_show)) {
        await to(this.nudgeAudioAlert.play())
      }
      this.emitWidgetColor(this.pusherColorMap[nudgeData.state_to_show]);
      this.nudgeHistoryEntry.nudge_uuid = nudgeData.nudge_uuid
      if (nudgeData.state_to_show == 'yellow') {
        this.nudgeHistoryEntry.start_time = Date()
      }
    },
    videoBannerReceived(payload) {
      if (!payload?.text) {
        return
      }
      const patientId = this.widgetModel?.monitor?.patient?.id
      if (!patientId || payload.patientId != patientId) {
        return
      }
      this.videoBanner = { active: true, text: payload.text }
    },
    clearVideoBanner() {
      this.videoBanner = { active: false, text: '' }
    },
    handleInference(inferenceData) {
      const videoWidth = this.$refs.remoteVideo.videoWidth,
        videoHeight = this.$refs.remoteVideo.videoHeight,
        widthScale = this.$refs.remoteVideo.clientWidth / videoWidth,
        heightScale = this.$refs.remoteVideo.clientHeight / videoHeight

      this.processedInferenceData = []
      for (const [inferenceType, objects] of Object.entries(inferenceData)) {
        if (Array.isArray(objects) && objects.length) {
          objects.forEach(object => {
            const x = object.xmin * videoWidth * widthScale,
              y = object.ymin * videoHeight * heightScale,
              width = (object.xmax * videoWidth * widthScale) - x,
              height = (object.ymax * videoHeight * heightScale) - y,
              labelType = INFERENCE_TO_LABEL_TYPES[inferenceType],
              color = LABEL_COLORS[labelType];

            const correctCoordinates = [x, y, width, height].every(val => {
              return parseFloat(val) >= 0
            });
            if (correctCoordinates) {
              this.processedInferenceData.push({ x, y, width, height, color })
            }
          })
        }
      }
    },
    // enableInference() {
    //   this.room.enableInference();
    //   this.isInferenceEnabled = true;
    // },
    // disableInference() {
    //   this.room.disableInference();
    //   this.isInferenceEnabled = false;
    // },
    connectParticipantMedia(participant) {
      // NOTE: this may be called multiple times for the same participant
      const isPatient = participant.isPatient;
      logger.debug('participant media connected', { peer_id: participant.participantId, is_patient: isPatient });

      // a late track event after stopVideo() must not attach the stream: the video element is
      // being removed by the template's v-if, and a detached element with a live srcObject keeps
      // playing natively, pinning the stream/participant/PC chain against GC
      if (!this.isConnecting && !this.isVideoConnected) {
        logger.debug('ignoring participant media, video already stopped', { peer_id: participant.participantId });
        return;
      }

      if (isPatient) {
        if (!this.$refs.remoteVideo) {
          logger.warn('remote video element not found', { monitor_id: this.widgetModel?.monitorId });
          return;
        }
        this.patientParticipant = participant
        if (participant.mediaStream == null) {
          logger.warn('patient media stream is null', { peer_id: participant.participantId });
          return;
        }
        if (this.$refs.remoteVideo.srcObject !== participant.mediaStream) {
          logger.info('patient stream attached', { stream_id: participant.mediaStream.id });
          this.$refs.remoteVideo.srcObject = participant.mediaStream
        }
        if (!this.isVideoConnected) {
          // signaling delivered the track; give ICE/DTLS its own window before timing out
          this.armConnectionTimeout()
        }
      }
    },
    armConnectionTimeout() {
      clearTimeout(this.connectionTimeout)
      this.connectionTimeout = setTimeout(() => {
        if (!this.isVideoConnected) {
          this.disconnectReason = END_REASONS.TIMEOUT
          this.stopVideo()
          this.showError('Video connection error. (ERR:CONNECT_TIMEOUT)')
          this.logLiveVideo(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
        }
      }, 20000);
    },
    onPatientMediaReady(webrtcCallId) {
      if (this.isVideoConnected) {
        return
      }
      this.isConnecting = false;
      this.isVideoConnected = true
      this.setConnectedMonitors({ isConnected: true, monitorId: this.widgetModel.monitorId })
      clearTimeout(this.connectionTimeout);
      this.autoRetryCount = 0
      this.errorText = null
      this.connectionTracker.connect(this.widgetModel.monitorId, this.$helpers.getMonitoringSource(), this.$store.state.uniqueSessionId, webrtcCallId)
    },
    async toggleIR() {
      const postData = {
        patient_monitor_id: this.widgetModel.monitorId
      };
      to(this.http.post('/videos/toggle_ir', postData, {
        handleErrors: false
      }))
    },
    logMonitor(type, monitorId = this.widgetModel.monitorId) {
      this.$logger.logPatientMonitor(type, monitorId, this.$helpers.getMonitoringSource())
    },
    logLiveVideo(type) {
      this.$logger.logLiveVideo(type, 'monitor', this.widgetModel?.monitor?.patient?.id, this.widgetModel.monitorId, null, this.widgetModel.monitor.video_device.ptz_enabled)
    },
    async volumeChanged(volume) {
      this.widgetModel.monitor.video_device.state = this.widgetModel.monitor.video_device.state || {}
      this.widgetModel.monitor.video_device.state.metrics = this.widgetModel.monitor.video_device.state.metrics || {}
      this.widgetModel.monitor.video_device.state.metrics.volume = await setVolume(volume, this.http, this.widgetModel.monitor.video_device.id);
      this.emitActions(this.$options.widget.actions)
    },
    toggleReadOnlyLabeling(state) {
      this.$nextTick(() => {
        this.labelingBoardReloadKey = Math.random()
        this.isReadOnlyLabeling = state
        this.isLabelingActive = state
      })
    },
    cancelLabelingBoard() {
      if (this.readOnlySafetyZoneEnabled) {
        if (this.nudgeEnabled) {
          this.isLabelingActive = true
          this.isReadOnlyLabeling = true
          setTimeout(() => {
            this.labelingBoardReloadKey = Math.random()
            this.shouldUpdateVirtualFence = false
          }, this.shouldUpdateVirtualFence ? 3000 : 0)
        } else {
          this.isLabelingActive = false
        }
      } else {
        this.isLabelingActive = false
      }
    },
    deviceStatusChanged({ video_device_id, status }) {
      const videoDevice = this.widgetModel.monitor.video_device
      if (video_device_id && status && videoDevice.id == video_device_id) {
        this.widgetModel.monitor.video_device.status = status
      }
    },
    async cardHoverEventHandler(widgetModel) {
      if (this.widgetModel.monitorId == widgetModel.monitorId && this.nudgeHistoryEntry.start_time) {
        this.nudgeHistoryEntry.end_time = Date()
        this.nudgeHistoryEntry.patient_monitor_id = widgetModel.monitorId
        this.nudgeHistoryEntry.source = this.$helpers.getMonitoringSource()

        await to(this.$http.post(`/patient_monitors/${widgetModel.monitorId}/nudge_confirmation`, this.nudgeHistoryEntry, {
          skipLoading: true,
          handleErrors: true
        }))

        this.emitWidgetColor(this.pusherColorMap.none, false);

        this.nudgeHistoryEntry = {}
      }
    },

    // status 'meeting-started' or  'meeting-ended'
    meetingStatusChanged({ patient_monitor_id, video_device_id, status }) {
      if (patient_monitor_id && patient_monitor_id == this.widgetModel.monitor.id || video_device_id && this.widgetModel.monitor.video_device?.id == video_device_id) {
        this.isMeetingActive = status == 'meeting-started'
      }
    },
    closeRecordingDialog() {
      this.showRecordingDialog = false;
    },
    async startRecording(form) {
      const [err] = await to(this.$http.post(`/patient_monitors/${this.widgetModel.monitor.id}/recording`, form))
      if (!err) {
        this.closeRecordingDialog();
        this.$buefy.toast.open({ message: 'Recording started', type: 'is-success' });
      }
    },
    // Starts the hidden two-way-video meeting to the kiosk so the patient sees the observer's camera.
    // No dialog: mirrors Monitoring.vue#toggleVideoOverlay by fetching /meetings/new for the
    // authoritative room_id, then builds the meeting from data already on widgetModel.monitor. The
    // meeting joins mic-muted (kioskVideoSettings.startMuted) so the observer's audio stays on the
    // monitoring connection (one live mic, no echo) while their video streams to the kiosk.
    async startKioskVideo() {
      if (this.showKioskVideo) {
        return;
      }
      // don't coexist with a visible PatientOverlayVideo meeting — they share global WebrtcView DOM
      // ids and LiveVideoStore state and would cross-corrupt each other's video
      if (this.liveVideoOverlayOpen) {
        logger.info('skipping two-way kiosk meeting, video overlay already open', { monitor_id: this.widgetModel?.monitorId })
        return;
      }
      const m = this.widgetModel.monitor
      const patientId = m.patient_id || m.patient?.id
      const [err, response] = await to(this.http.get(`/meetings/new?patient_id=${patientId}`, { handleErrors: true, skipLoading: true }))
      if (err) {
        // /meetings/new is authorized by MeetingPolicy#new? (room membership, patient active). With
        // handleErrors the rejection is otherwise swallowed; log it so a silent no-op is diagnosable.
        logger.warn('two-way kiosk meeting not started, meetings/new was rejected', { monitor_id: this.widgetModel?.monitorId, patient_id: patientId }, err)
        return;
      }
      const { data } = response
      // the mic may have been muted (or another tile unmuted) while /meetings/new was in flight —
      // abort so we don't start an orphaned meeting that no mute toggle will tear down
      if (!this.localAudioEnabled) {
        return;
      }
      this.kioskMeetingMetaData = {
        device: m.video_device,
        patient: m.patient,
        patient_monitor: m,
        room_id: data.room_id,
        kiosk_mode: data.kiosk_mode,
        is_ptz: false,
        test_mode: false,
      }
      this.kioskVideoSettings = {
        activateVideo: true,
        doorKnock: false,
        isPreview: false,
        startMuted: true, // video-only: observer audio stays on the monitoring connection
        visitorName: this.$store.state.currentUser.display_name || this.$store.state.currentUser.name,
        selectedVideoReason: null,
        selectedVideoReasons: [],
      }
      this.showKioskVideo = true
    },
    async stopKioskVideo() {
      // mark as an intentional teardown so the @close the WebrtcView emits during stopVideo() isn't
      // mistaken for an unexpected self-disconnect (which would trigger a restart)
      this.kioskStopping = true
      await this.$refs.kioskWebrtc?.stopVideo?.()
      this.showKioskVideo = false
      this.kioskStopping = false
      this.kioskRestartAttempts = 0
    },
    // Fires when the meeting closes. If WE asked for it (mute/disconnect/unmount) just let teardown
    // proceed; if the WebrtcView closed itself (timeout, lost patient, media/connection failure)
    // while the observer is still talking, recover with a capped number of restarts.
    onKioskClosed() {
      if (this.kioskStopping) {
        return
      }
      this.showKioskVideo = false
      if (!this.localAudioEnabled || !this.kioskTwoWayVideoEnabled) {
        return
      }
      if (this.kioskRestartAttempts < 2) {
        this.kioskRestartAttempts += 1
        logger.warn('two-way video meeting closed unexpectedly, restarting', { monitor_id: this.widgetModel?.monitorId, attempt: this.kioskRestartAttempts })
        this.$nextTick(() => {
          if (this.localAudioEnabled && this.kioskTwoWayVideoEnabled) {
            this.startKioskVideo()
          }
        })
      } else {
        logger.error('two-way video meeting failed repeatedly, giving up', { monitor_id: this.widgetModel?.monitorId, attempts: this.kioskRestartAttempts })
      }
    },
    // Once the meeting is up, silence the meeting's INCOMING audio (the patient's audio already
    // plays through the monitoring connection) so the observer doesn't hear the patient twice. The
    // outgoing mic is already muted at capture via kioskVideoSettings.startMuted.
    onKioskMeetingId() {
      this.$nextTick(() => {
        this.$refs.kioskWebrtc?.$el?.querySelectorAll('video')?.forEach(v => { v.muted = true })
      })
    }
  },
  widget: {
    title: "Live Video",
    actions: [
      {
        title: 'Edit Safety Zone',
        icon: 'mdi mdi-vector-polygon',
        hasBottomSeparator: true,
        isVisible() {
          return this.isVideoConnected && !this.ptzMode
        },
        handler: function () {
          if (this.isReadOnlyLabeling) {
            this.isLabelingActive = true
            this.isReadOnlyLabeling = false
            this.labelingBoardReloadKey = Math.random()
          } else {
            this.isLabelingActive = !this.isLabelingActive
            if (!this.isLabelingActive) {
              this.cancelLabelingBoard()
            }
          }
        }
      },
      // device version and name
      {
        component() {
          return VideoDeviceVersion
        },
        componentAttributes() {
          if (!this.widgetModel?.monitor?.video_device) {
            return {}
          }
          const { name, version, id } = this.widgetModel.monitor.video_device
          return { name, version, id }
        },
        icon: 'mdi mdi-video-outline',
        isVisible() {
          return this.widgetModel.monitor && this.widgetModel.monitor.video_device
        },
        handler: function () {
          if (this.$can('Edit', 'VideoDevice')) {
            const route = this.$router.resolve({
              name: 'hospital.video_devices.edit',
              params: { id: this.widgetModel.monitor.video_device.id }
            })
            window.open(route.href, '_blank')
          }
        }
      },
      {
        title() {
          if (this.widgetModel.monitor && this.widgetModel.monitor.video_device) {
            const { version, id } = this.widgetModel.monitor.video_device
            return `Version ${version} (${id})`
          }
          return ''
        },
        icon: 'mdi mdi-information-outline',
        isVisible() {
          return this.widgetModel.monitor && this.widgetModel.monitor.video_device
        },
        handler: function () {
          if (this.$can('Edit', 'VideoDevice')) {
            const route = this.$router.resolve({
              name: 'hospital.video_devices.edit',
              params: { id: this.widgetModel.monitor.video_device.id }
            })
            window.open(route.href, '_blank')
          }
        }
      },
      {
        title() {
          return `RSSI: ${this.wifiSignalStrength}`
        },
        icon: 'mdi mdi-wifi',
        isClickable: false,
        isVisible() {
          return this.wifiSignalStrength
        }
      },
      {
        title: 'Stop Monitoring',
        icon: 'mdi mdi-stop-circle-outline',
        hasTopSeparator: true,
        isVisible() {
          return this.$can('Edit', 'PatientMonitor') && this.widgetModel.monitor && ['active', 'paused'].includes(this.widgetModel.monitor.status)
        },
        handler: function () {
          EventBus.$emit(EVENTS.STOP_MONITORING, this.widgetModel.monitor.id)
        }
      },
      {
        title: 'Reboot Device',
        icon: 'mdi mdi-refresh',
        isVisible: function () {
          return this.widgetModel?.monitor?.video_device.kind != 'trainingvideo' &&
            this.widgetModel?.monitor?.video_device.credentials_generated_at !== null &&
            this.$can('View', 'PatientMonitor') &&
            !this.widgetModel.settings.isHistorical &&
            this.widgetModel.name == 'live-video' &&
            !this.isDeviceOffline
        },
        handler: function () {
          const dialog = this.$buefy.dialog.confirm({
            title: 'Confirm Device Reboot',
            message: 'This action will reboot the LookDeep Video Device. All users connected to this device will be temporarily disconnected. It may take 1-5 minutes to return online. Do you wish to continue?',
            cancelText: 'Cancel',
            confirmText: 'Confirm',
            focusOn: 'cancel',
            onConfirm: async () => {
              const successToast = () => {
                this.$buefy.toast.open({
                  message: 'Reboot command sent',
                  type: 'is-success'
                });
              }
              const rebootSuccess = () => {
                successToast()
                this.deviceRebootCommandSent = true
                this.setupBadges()
              }
              if (this.widgetModel?.monitor?.video_device.kind != 'trainingvideo') {
                const [err] = await to(this.$http.post(`/patient_monitors/${this.widgetModel.monitor.id}/reboot`))
                if (!err) {
                  if (this.widgetModel.monitor.video_device?.state?.pending_software_update) {
                    this.$logger.logVideoDevice(LOG_TYPES.VIDEO_DEVICE.UPDATE_REBOOT, this.widgetModel.monitor.video_device.id)
                  }
                  rebootSuccess()
                }
              } else {
                rebootSuccess()
              }
              dialog.close()
            }
          })
        }
      },
      {
        title: 'Mute/Unmute TV',
        icon: 'mdi mdi-volume-mute',
        isVisible: function () {
          return this.canSendIrCommands
        },
        handler: function () {
          this.sendIrMuteCommand();
        }
      },
      {
        title: 'Disconnect',
        icon: 'mdi mdi-phone-hangup',
        hasBottomSeparator: true,
        isVisible: function () {
          return this.widgetModel.monitor && this.widgetModel.monitor.status == 'active' && this.isVideoConnected
        },
        handler: function () {
          this.stopVideo();
        }
      },
      {
        title: 'Switch Night Mode',
        icon: 'mdi mdi-flashlight',
        isVisible: function () {
          return false
        },
        handler: function () {
          this.toggleIR()
        }
      },
      {
        title() {
          return this.isInferenceEnabled ? 'Disable Inference' : 'Enable Inference'
        },
        icon: 'mdi mdi-eye',
        isVisible: function () {
          return this.$store.state.currentUser.enabled_flags.includes('live_inference') && this.widgetModel.monitor && this.widgetModel.monitor.status == 'active' && this.isVideoConnected
        },
        handler: function () {
          this.processedInferenceData = []
          if (this.isInferenceEnabled) {
            this.disableInference();
          } else {
            this.enableInference();
          }
          this.emitActions(this.$options.widget.actions)
        }
      },
      {
        title: 'Volume',
        icon: 'mdi mdi-volume-high',
        isClickable: false,
        parentContainerClass: 'is-block',
        component() {
          return LvuVolumeControl
        },
        componentAttributes() {
          if (!this.widgetModel.monitor) {
            return {}
          }
          return {
            onVolumeChange: this.volumeChanged,
            modelValue: this.widgetModel.monitor.video_device?.config?.device?.volume || 100,
            id: this.widgetModel.monitor.video_device.id
          }
        },
        isVisible() {
          return this.isVideoConnected && this.$can('AccessLiveVideo', 'Meeting') || this.$can('AccessLiveVideo', 'PatientMonitor');
        },
      },
      // {
      //   title: 'Translation',
      //   icon: 'mdi mdi-translate-variant',
      //   isVisible() {
      //     return this.canEditAimeeConfiguration;
      //   },
      //   handler() {
      //     this.isTranslationSettingsEnabled = true
      //   }
      // },
      {
        title: 'Start Recording',
        icon: 'mdi mdi-record-circle',
        isVisible() {
          return this.isVideoConnected && this.$can('Create', 'Recording')
        },
        handler: function () {
          this.showRecordingDialog = true
        }
      },
      {
        title: 'Report a problem',
        icon: 'mdi mdi-alert-circle-outline',
        hasTopSeparator: true,
        isVisible() {
          return !!this.widgetModel?.monitor?.video_device?.id
        },
        handler: function () {
          this.showVideoIssueDialog = true
        }
      }
    ],
    guard: function () {
      const permission = this.$can('AccessLiveVideo', 'PatientMonitor')
      if (!permission) {
        this.setErrorMode()
        this.showError("You need permission: 'Access Live Video' to view this block")
      }
      return permission
    }
  }
}
</script>

<style scoped lang="scss">
.absolute-center {
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.error-message {
  margin-bottom: 10px;
  text-align: center;
  font-size: 14px;
  color: var(--ld-status-warning);
}

.notifications-bar {
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  z-index: 18;

  .notifications-bar__top {
    display: flex;
  }

  .notifications-bar__bottom {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  >div {
    width: 100%;

    span {
      flex: 1 1;
      text-align: center;
      padding: 2px 5px;
      font-size: 11px;
      letter-spacing: 2px;
      font-weight: bold;
      color: #fff;
    }
  }

  .patient-mic.patient-mic {
    background: green;
  }

  .local-mic.local-mic.local-mic {
    background: red;
  }

  .global-mic {
    min-width: fit-content;
    background: dodgerblue;
  }

  .wake-word {
    background: linear-gradient(to right, #685bc7 0, #a438a8 50%, #685bc7 100%);
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .video-visit {
    background: #4caf50;
    text-transform: uppercase;
  }

  .away-message {
    background: #FFC107;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: center;
    padding: 2px 3px;
    font-size: 14px;
    width: 100%;
  }
}

.overlay-buttons {
  display: none;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 18;

  button {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: #4fc3f78f;
    color: #fff;
    border: solid 1px #7dd7ff;
    margin: 0 10px;
    font-size: 20px;

    transition: all 0.1s linear;

    &:hover {
      background-color: #4fc3f7;
      color: #fff;
    }

    i {
      padding-top: 5px;
    }
  }
}

.live-video-widget:hover {
  .overlay-buttons {
    display: flex;
    position: absolute;
    top: 0;
  }

  .video-ptz.without-popup {
    opacity: 1
  }

  :deep(.blinds__control) {
    display: block;
  }

  .scale-reset-button {
    display: block;
  }
}

.live-video-widget {
  //display: flex;
  //align-items: center;
  //justify-content: center;
  display: block;
  position: relative;
  overflow: hidden !important;
}

// hidden two-way-video meeting — mounted off-screen (NOT display:none, which can skip rendering of
// the local-view element the camera capture relies on) so the observer never sees it.
.kiosk-video-offscreen {
  position: absolute;
  width: 1px;
  height: 1px;
  left: -9999px;
  top: 0;
  overflow: hidden;
  pointer-events: none;
}

:deep() {
  #webrtc-live-video {
    width: 100%;
    overflow: hidden;
    z-index: 1;
  }

  #webrtc-live-video video {
    width: 100%;
  }
}

.video-ptz {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  margin: auto;
  z-index: 999;

  &.without-popup {
    opacity: 0;
    margin: initial;
    right: 10px;
    left: initial;
    bottom: 40px;
  }
}

.inference-data {
  >div {
    position: absolute;
    border: 2px solid red;
  }
}

#volume-container {
  width: 250px;
  height: 50px;
  position: absolute;
  margin: 0 auto;
  bottom: 40px;

  // i {
  // 	position: absolute;
  // 	margin-top: -6px;
  // 	color: #666;
  // }

  // i.fa-volume-down {
  // 	margin-left: -8px;
  // }

  // i.fa-volume-up {
  // 	margin-right: -8px;
  // 	right: 0;
  // }
}

#volume-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  p {
    color: #fff
  }

  button {
    border-radius: 50%;
    background-color: transparent;
    color: #fff;
    z-index: 20;
  }
}

#webrtc-live-video {
  position: relative;
}

.connect-button i {
  font-size: 19px;
  color: #ffa748;
}

video {
  border-radius: 0 0 4px 4px;
}

.special-error :deep() {
  .special-error__icon {
    margin: 8px 10px 0 0;
  }

  .media-content {
    font-size: 14px;
    line-height: 17px;
    display: flex;
    align-items: center;
    text-align: left;
  }
}

.scale-reset-button {
  display: none;
  bottom: 40px;
  background: none;
  position: absolute;
  width: 30px;
  height: 30px;
  z-index: 19;
  right: 10px;
  border-radius: 100%;
  border: 1px solid #ffffff;
  cursor: pointer;
  color: #FFFFFF;
  font-size: 20px;
  padding-top: 3px;
}

.translate-button::before,
.translate-button::after,
.translate-button span::before,
.translate-button span::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #4fc3f7;
  border-radius: 50%;
  z-index: -1;
  opacity: 0;
  transform: scale(1);
  pointer-events: none;
}

.translate-button.translate-button--animated::before {
  animation: ripple 1.8s infinite linear;
}

.translate-button.translate-button--animated::after {
  animation: ripple 1.8s infinite linear 0.45s;
}

.translate-button span {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.translate-button.translate-button--animated span::before {
  animation: ripple 1.8s infinite linear 0.9s;
}

.translate-button.translate-button--animated span::after {
  animation: ripple 1.8s infinite linear 1.35s;
}

@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.4;
  }

  100% {
    transform: scale(2.6);
    opacity: 0;
  }
}

.openai-transcript {
  position: absolute;
  bottom: 35px;
  max-width: 80%;
  left: 0;
  right: 0;
  background: #00000073;
  border-radius: 4px;
  color: #fff;
  padding: 5px;
  margin: auto;
  z-index: 9;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
}

.alarm-stop-button {
  width: 30px;
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;

  i {
    font-size: 17px;
  }
}
</style>
